// Pricing route: sortable, filterable pricing comparison table with biggest mover card.

import React, { useEffect, useState } from 'react';
import { t } from '../lib/i18n';
import type { Lang } from '../lib/i18n';
import { api } from '../lib/api';
import type { PricingData, DiffsData } from '../lib/api';
import { PricingTable } from '../components/PricingTable';
import { BiggestMoverCard } from '../components/BiggestMoverCard';
import { PricingDataSourcesPanel } from '../components/PricingDataSourcesPanel';
import { formatWeekRange } from '../components/Header';

interface PricingProps {
  lang: Lang;
  currentWeek: number | null;
  code: string;
}

export function Pricing({ lang, currentWeek, code }: PricingProps) {
  const [pricingData, setPricingData] = useState<PricingData | null>(null);
  const [diffsData, setDiffsData] = useState<DiffsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentWeek) return;

    setLoading(true);
    setError(null);

    Promise.all([
      api.pricing.byWeek(currentWeek),
      api.diffs.byWeek(currentWeek),
    ])
      .then(([pricing, diffs]) => {
        setPricingData(pricing);
        setDiffsData(diffs);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [currentWeek]);

  if (!currentWeek) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-500 text-sm">
        {t('loading', lang)}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-500 text-sm">
        {t('loading', lang)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-500 text-sm">
        {error}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <h1 className="text-xs font-medium text-neutral-500 uppercase tracking-wider">
          {t('pricingComparison', lang)}
        </h1>
        {currentWeek && (
          <span className="text-xs text-neutral-500 tabular-nums">
            {t('weekLabel', lang)} {currentWeek % 100}
            <span className="text-neutral-300"> · {formatWeekRange(currentWeek)}</span>
          </span>
        )}
      </div>

      {/* Methodology block — answers the product-team question: "Burada listelenen planlar neye göre geliyor?"
          Make it explicit: where the plans come from, how often, and how outliers are handled. */}
      <div className="card p-4">
        <div className="text-xs font-medium text-neutral-800 mb-1.5">
          {lang === 'tr' ? 'Bu fiyatlar nereden geliyor?' : 'Where do these prices come from?'}
        </div>
        <p className="text-xs text-neutral-600 leading-relaxed">
          {lang === 'tr'
            ? 'Her Pazartesi sabahı 28 sağlayıcının kendi fiyat sayfasına bakılır ve plan adı, bölge, kaç GB, kaç gün ve dolar fiyatı alınır. Her satırın hangi sayfadan geldiği saklanır; “Kaynak” linkine tıklayarak fiyatı kendiniz teyit edebilirsiniz. Kaynağını göremediğimiz fiyat tabloya hiç girmez. Bir fiyat bir haftada iki katına çıkmış gibi görünüyorsa bu genellikle sayfanın değişmesinden kaynaklanır, gerçek zam değildir; bu yüzden aşırı büyük farklar manşete çıkarılmaz, işaretlenir.'
            : 'Every Monday morning we check the pricing page of each of the 28 providers and take the plan name, region, how many GB, how many days, and the dollar price. Every row keeps the page it came from — click “Kaynak” to confirm the price yourself. If we can’t see a price at its source, it never enters the table. When a price looks like it doubled in a week, that is usually the page changing rather than a real increase, so unusually large swings are flagged instead of headlined.'}
        </p>
      </div>

      {/* Biggest mover */}
      {diffsData?.biggest_mover && (
        <BiggestMoverCard diff={diffsData.biggest_mover} lang={lang} code={code} />
      )}

      {/* Pricing table */}
      {pricingData ? (
        <>
          <PricingTable
            plans={pricingData.plans}
            providers={pricingData.providers}
            diffs={diffsData?.diffs}
            lang={lang}
            code={code}
          />
          <PricingDataSourcesPanel
            plans={pricingData.plans}
            providers={pricingData.providers}
            lang={lang}
          />
        </>
      ) : (
        <div className="card p-8 text-center text-neutral-500 text-sm">{t('noData', lang)}</div>
      )}
    </div>
  );
}
